import React, { useCallback } from 'react';
import {
  AddOperandButton,
  Filter,
  getRootOperator,
  isIngressExp,
  OperatorType,
  shouldRenderWithFieldFilterBox,
} from '@fabric-ui/components';
import { emptyObject } from '@fabric-ui/utils';
import { Box } from '@mui/material';
import { isNullOrUndefined } from '@utils/utils';
import { useExpressionBuilder } from './ExpressionBuilderContext';
import { FieldFilterBoxBase } from './FieldFilterBoxBase';
import { useFilterConfig } from './FilterContext';
import { useExpressionOperations } from './hooks/useExpressionOperations';
import { LogicalOperatorType } from './types';

export type FieldOption = { value: string; disabled?: boolean; [key: string]: any };

interface RenderOperatorProps {
  filter: Filter;
  setFilter: any;
  rootOperator: LogicalOperatorType;
  level?: number;
  onToggleLogicalOperators: () => void;
}

export const hasEmptyValueOperand = (filter: Filter): boolean => {
  const operator = getRootOperator(filter);
  if (operator === 'and' || operator === 'or') {
    const operands = filter[operator]?.operands;
    return !!operands?.some(op => {
      const condition = op.expression?.stringCondition;
      return condition && Object.values(condition).some(v => v === '');
    });
  }
  return false;
};

export const hasMultipleInOperands = (filter: Filter): boolean => {
  const operator = getRootOperator(filter);
  if (operator !== 'and' && operator !== 'or') return false;
  const operands = filter[operator]?.operands;
  if (!operands || operands.length <= 1) return false;
  return operands.filter(op => op.expression?.stringCondition?.in).length > 1;
};

const operatorColors = {
  and: 'var(--color-surface-interactive-primary-default)',
  or: 'var(--color-border-status-warning-secondary)',
};

interface RenderOperatorItemProps {
  addInline: (index: number) => void;
  deleteAt: (index: number) => void;
  fieldOnly: boolean;
  hasListTab: boolean;
  icon: React.ReactNode;
  index: number;
  innerFilter: Filter;
  isLastOperand: boolean;
  isOnlyOperand: boolean;
  level: number;
  onToggleLogicalOperators: () => void;
  replaceAt: (index: number, value: Filter) => void;
  rootOperator: LogicalOperatorType;
  shouldShowIcon: ((filter: Filter) => boolean) | undefined;
}

const RenderOperatorItem: React.FC<RenderOperatorItemProps> = React.memo(
  ({
    addInline,
    deleteAt,
    fieldOnly,
    hasListTab,
    icon,
    index,
    innerFilter,
    isLastOperand,
    isOnlyOperand,
    level,
    onToggleLogicalOperators,
    replaceAt,
    rootOperator,
    shouldShowIcon,
  }) => {
    const { disabled, isVertical, size } = useFilterConfig();
    const setInnerFilter = useCallback((value: Filter) => replaceAt(index, value), [replaceAt, index]);
    const onDelete = useCallback(() => deleteAt(index), [deleteAt, index]);
    const addLogicalExpression = useCallback(() => addInline(index), [addInline, index]);
    const isIngressExpression = isIngressExp(innerFilter);

    return (
      <Box sx={{ borderLeft: `2px solid ${operatorColors[rootOperator]}`, ml: level * 2 }}>
        {!isLastOperand && (
          <Box className={isVertical ? '-my-1 ml-[105px] flex items-start' : '-my-1 ml-[52.5px] flex items-start'}>
            <AddOperandButton
              style={{ translate: '-50%' }}
              size={size}
              operator={rootOperator}
              onClick={onToggleLogicalOperators}
              disabled={disabled}
            />
          </Box>
        )}
        {shouldRenderWithFieldFilterBox(innerFilter, hasListTab) &&
        !(hasListTab && hasEmptyValueOperand(innerFilter)) &&
        !(hasListTab && hasMultipleInOperands(innerFilter)) ? (
          <Box className="flex items-center justify-start gap-2">
            <FieldFilterBoxBase
              rootOperator={rootOperator as OperatorType}
              filter={innerFilter}
              setFilter={setInnerFilter}
              level={level}
              onDelete={onDelete}
              addLogicalExpression={fieldOnly || isOnlyOperand ? undefined : addLogicalExpression}
              isIngressExp={isIngressExpression}
            />
            {(isNullOrUndefined(shouldShowIcon) || shouldShowIcon?.(innerFilter)) && icon}
          </Box>
        ) : (
          <RenderOperator
            filter={innerFilter}
            setFilter={setInnerFilter}
            rootOperator={getRootOperator(innerFilter) as LogicalOperatorType}
            level={level + 1}
            onToggleLogicalOperators={onToggleLogicalOperators}
          />
        )}
      </Box>
    );
  }
);

const RenderOperator: React.FC<RenderOperatorProps> = ({ filter, setFilter, rootOperator, level = 0, onToggleLogicalOperators }) => {
  const {
    defaultField,
    supportParser,
    canDeleteLast,
    relationsProps = emptyObject,
    fieldOnly = false,
    hasListTab = false,
    shouldShowIcon,
    icon,
  } = useExpressionBuilder();
  const { addInline, deleteAt, replaceAt } = useExpressionOperations(filter, setFilter, {
    canDeleteLast,
    defaultEmptyFilter: relationsProps.defaultEmptyFilter,
    defaultField,
    hasListTab,
    level,
    rootOperator,
    supportParser,
  });

  const operandsList = filter[rootOperator]?.operands;
  return operandsList?.map((innerFilter, index) => (
    <RenderOperatorItem
      // eslint-disable-next-line react/no-array-index-key
      key={index}
      addInline={addInline}
      deleteAt={deleteAt}
      fieldOnly={fieldOnly}
      hasListTab={hasListTab}
      icon={icon}
      index={index}
      innerFilter={innerFilter}
      isLastOperand={!(index > 0 && operandsList.length > index)}
      isOnlyOperand={operandsList.length === 1}
      level={level}
      onToggleLogicalOperators={onToggleLogicalOperators}
      replaceAt={replaceAt}
      rootOperator={rootOperator}
      shouldShowIcon={shouldShowIcon}
    />
  ));
};

export default RenderOperator;
